class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    // Check if this is a free gift item - prevent quantity changes
    const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
    if (lineItem) {
      const isFreeGift = lineItem.querySelector('[data-free-gift-item]') !== null ||
        (lineItem.textContent && lineItem.textContent.includes('FREE GIFT'));
      
      if (isFreeGift) {
        // Free gift quantity must always be 1
        const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        if (quantityElement) {
          quantityElement.value = 1;
        }
        return;
      }
    }

    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        
        // Gift validation will be triggered by the cartUpdate event handler
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      lineItemError.querySelector('.cart-item__error-text').innerHTML = message || '';
      if (message && message.trim() !== '') {
        lineItemError.removeAttribute('hidden');
      } else {
        lineItemError.setAttribute('hidden', '');
      }
    }

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// Manages adding or removing the configured free gift relative to the cart subtotal.
(() => {
  const CONFIG_SELECTOR = '[data-free-gift-config]';
  const CHECKOUT_BUTTON_SELECTOR = '#CartDrawer-Checkout';
  let isProcessing = false;
  let refreshTimeout = null;
  let isRefreshing = false;
  let checkoutWatchdog = null;

  const isFreeGiftEnabled = () => {
    const checkoutButton = document.querySelector(CHECKOUT_BUTTON_SELECTOR);
    return checkoutButton && checkoutButton.hasAttribute('data-free-gift-enabled');
  };

  const disableCheckoutButton = () => {
    if (!isFreeGiftEnabled()) {
      return;
    }
    const checkoutButton = document.querySelector(CHECKOUT_BUTTON_SELECTOR);
    if (checkoutButton && !checkoutButton.disabled) {
      checkoutButton.disabled = true;
      checkoutButton.setAttribute('aria-busy', 'true');
      checkoutButton.classList.add('is-loading');
    }
    // Safety watchdog: never leave checkout disabled when no gift operation is in flight.
    if (checkoutWatchdog) clearTimeout(checkoutWatchdog);
    checkoutWatchdog = setTimeout(() => {
      if (!isProcessing && !isRefreshing) {
        enableCheckoutButton();
      }
    }, 3000);
  };

  const enableCheckoutButton = () => {
    if (!isFreeGiftEnabled()) {
      return;
    }
    const checkoutButton = document.querySelector(CHECKOUT_BUTTON_SELECTOR);
    if (checkoutButton) {
      // Only enable if cart is not empty
      const cartDrawer = document.querySelector('cart-drawer');
      const isEmpty = cartDrawer && cartDrawer.classList.contains('is-empty');
      if (!isEmpty) {
        checkoutButton.disabled = false;
        checkoutButton.removeAttribute('aria-busy');
        checkoutButton.classList.remove('is-loading');
        if (checkoutWatchdog) {
          clearTimeout(checkoutWatchdog);
          checkoutWatchdog = null;
        }
      }
    }
  };

  const readConfig = () => {
    const node = document.querySelector(CONFIG_SELECTOR);
    if (!node) {
      return null;
    }

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('Failed to parse free gift config', error);
      return null;
    }
  };

  const fetchCartState = async () => {
    if (typeof routes === 'undefined' || typeof fetchConfig !== 'function') {
      return null;
    }

    try {
      const response = await fetch(`${routes.cart_url}.js`, fetchConfig());
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch cart state', error);
      return null;
    }
  };

  const refreshCartDrawer = (skipValidation = false) => {
    if (typeof routes === 'undefined') {
      return Promise.resolve();
    }

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      return Promise.resolve();
    }

    if (isRefreshing) {
      return Promise.resolve();
    }

    isRefreshing = true;

    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = ['cart-drawer-items', '.cart-drawer__footer', '[data-free-gift-config]'];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }
        // Trigger validation after refresh unless explicitly skipped
        if (!skipValidation) {
          setTimeout(() => {
            ensureGift();
          }, 100);
        } else {
          // If validation is skipped, ensure checkout is enabled if not processing
          if (!isProcessing) {
            enableCheckoutButton();
          }
        }
      })
      .catch((error) => {
        console.error('Failed to refresh cart drawer', error);
        // Re-enable checkout on error if not processing
        if (!isProcessing) {
          enableCheckoutButton();
        }
        return Promise.reject(error);
      })
      .finally(() => {
        isRefreshing = false;
      });
  };

  const publishSync = () => {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift-sync' });
    }
  };

  const validateGiftConstraints = (cartState, variantId) => {
    if (!cartState || !cartState.items) {
      return { isValid: true, needsFix: false, action: null };
    }

    const giftItems = cartState.items.filter(
      (item) => item.variant_id === variantId && item.properties && item.properties._auto_free_gift === 'true'
    );

    if (giftItems.length === 0) {
      return { isValid: true, needsFix: false, action: null };
    }

    // Check if gift quantity is more than 1
    const giftWithExcessQty = giftItems.find((item) => item.quantity > 1);
    if (giftWithExcessQty) {
      return {
        isValid: false,
        needsFix: true,
        action: 'reduceQuantity',
        lineKey: giftWithExcessQty.key,
      };
    }

    // Check if gift is alone in cart
    const nonGiftItems = cartState.items.filter(
      (item) => !(item.variant_id === variantId && item.properties && item.properties._auto_free_gift === 'true')
    );

    if (nonGiftItems.length === 0 && giftItems.length > 0) {
      return {
        isValid: false,
        needsFix: true,
        action: 'removeGift',
        lineKey: giftItems[0].key,
      };
    }

    return { isValid: true, needsFix: false, action: null };
  };

  const fixGiftQuantity = (lineKey) => {
    if (isProcessing || !lineKey || typeof fetchConfig !== 'function' || typeof routes === 'undefined') {
      return;
    }

    isProcessing = true;
    disableCheckoutButton();

    const body = JSON.stringify({
      id: lineKey,
      quantity: 1,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fix gift quantity');
        }
        return response.json();
      })
      .then((cartResponse) => {
        // Use cart response data directly for faster validation
        refreshCartDrawer(true); // Skip validation to prevent loop
        publishSync();
        
        // Use cart data from response if available
        if (cartResponse && cartResponse.items) {
          ensureGift(cartResponse);
        } else {
          // Fallback to fetching if response structure is different
          setTimeout(() => {
            if (!isProcessing) {
              ensureGift();
            }
          }, 50);
        }
      })
      .catch((error) => {
        console.error('Failed to fix gift quantity', error);
      })
      .finally(() => {
        isProcessing = false;
        enableCheckoutButton();
      });
  };

  const addGift = async (variantId) => {
    if (isProcessing || typeof fetchConfig !== 'function' || typeof routes === 'undefined') {
      return;
    }

    isProcessing = true;
    disableCheckoutButton();

    const body = JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: 1,
          properties: {
            _auto_free_gift: 'true',
          },
        },
      ],
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to add free gift');
        }
        return response.json();
      })
      .then(() => {
        // Refresh cart drawer immediately
        refreshCartDrawer(true); // Skip validation to prevent loop
        publishSync();
        
        // Fetch fresh cart state after drawer refresh completes
        setTimeout(() => {
          if (!isProcessing) {
            ensureGift();
          }
        }, 80);
      })
      .catch((error) => {
        console.error('Failed to add free gift', error);
      })
      .finally(() => {
        isProcessing = false;
        enableCheckoutButton();
      });
  };

  const removeGiftByKey = (lineKey) => {
    if (isProcessing || !lineKey || typeof fetchConfig !== 'function' || typeof routes === 'undefined') {
      return;
    }

    isProcessing = true;
    disableCheckoutButton();

    const body = JSON.stringify({
      id: lineKey,
      quantity: 0,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to remove free gift');
        }
        return response.json();
      })
      .then((cartResponse) => {
        // Use cart response data directly for faster validation
        refreshCartDrawer(true); // Skip validation to prevent loop
        publishSync();
        
        // Use cart data from response if available
        if (cartResponse && cartResponse.items) {
          ensureGift(cartResponse);
        } else {
          // Fallback to fetching if response structure is different
          setTimeout(() => {
            if (!isProcessing) {
              ensureGift();
            }
          }, 50);
        }
      })
      .catch((error) => {
        console.error('Failed to remove free gift', error);
      })
      .finally(() => {
        isProcessing = false;
        enableCheckoutButton();
      });
  };

  const ensureGift = async (cartDataFromEvent = null) => {
    if (isProcessing) {
      return;
    }

    const config = readConfig();
    if (!config || !config.enabled || !config.variantId) {
      enableCheckoutButton(); // Ensure checkout is enabled if gift is not configured
      return;
    }

    const variantId = Number(config.variantId);
    if (!variantId) {
      enableCheckoutButton();
      return;
    }

    // Disable checkout while validating
    disableCheckoutButton();

    // Use cart data from event if available, otherwise fetch
    let cartState = cartDataFromEvent;
    if (!cartState || !cartState.items) {
      cartState = await fetchCartState();
    }

    if (!cartState || !cartState.items) {
      // Fallback to config if cart fetch fails
      const qualifies = Boolean(config.qualifies);
      const giftPresent = Boolean(config.cartHasGift);
      const lineKey = config.giftLineKey;

      if (qualifies && !giftPresent) {
        addGift(variantId);
      } else if (!qualifies && giftPresent && lineKey) {
        removeGiftByKey(lineKey);
      } else {
        enableCheckoutButton();
      }
      return;
    }

    // Validate gift constraints first (using cart state we already have)
    const validation = validateGiftConstraints(cartState, variantId);
    if (!validation.isValid && validation.needsFix) {
      if (validation.action === 'reduceQuantity') {
        fixGiftQuantity(validation.lineKey);
        return;
      } else if (validation.action === 'removeGift') {
        removeGiftByKey(validation.lineKey);
        return;
      }
    }

    // Recalculate qualification based on current cart state
    const giftItems = cartState.items.filter(
      (item) => item.variant_id === variantId && item.properties && item.properties._auto_free_gift === 'true'
    );
    const giftPresent = giftItems.length > 0;
    const giftLineKey = giftPresent ? giftItems[0].key : null;

    // Calculate subtotal excluding gift
    let subtotalExcludingGift = cartState.total_price || 0;
    if (giftPresent) {
      const giftLinePrice = giftItems.reduce((sum, item) => sum + (item.line_price || 0), 0);
      subtotalExcludingGift = Math.max(0, subtotalExcludingGift - giftLinePrice);
    }

    // Get threshold from config (already in cents)
    const threshold = Number(config.threshold) || 0;
    const qualifies = threshold > 0 && subtotalExcludingGift >= threshold;

    // Ensure gift is not alone
    const nonGiftItems = cartState.items.filter(
      (item) => !(item.variant_id === variantId && item.properties && item.properties._auto_free_gift === 'true')
    );

    if (qualifies && !giftPresent && nonGiftItems.length > 0) {
      addGift(variantId);
    } else if ((!qualifies || nonGiftItems.length === 0) && giftPresent && giftLineKey) {
      removeGiftByKey(giftLineKey);
    } else if (giftPresent && giftItems[0].quantity !== 1) {
      // Ensure quantity is always 1
      fixGiftQuantity(giftLineKey);
    } else {
      // Validation complete, no changes needed
      enableCheckoutButton();
    }
  };

  const handleCartUpdate = (event) => {
    if (isProcessing || (event && event.source === 'free-gift-sync')) {
      return;
    }

    // Clear any pending refresh
    if (refreshTimeout) {
      clearTimeout(refreshTimeout);
    }

    // Use cart data from event if available for faster processing
    const cartData = event && event.cartData ? event.cartData : null;
    
    // Small delay only if we need to fetch cart state (no event data)
    const delay = cartData ? 0 : 50;
    
    refreshTimeout = setTimeout(() => {
      ensureGift(cartData);
    }, delay);
  };

  document.addEventListener('DOMContentLoaded', () => {
    // Initial validation - checkout will be enabled after validation completes
    ensureGift();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
    }

    // Fallback: ensure checkout is enabled after a delay if no processing occurred
    setTimeout(() => {
      if (!isProcessing && !isRefreshing) {
        enableCheckoutButton();
      }
    }, 1000);
  });
})();
